<script lang="ts">
  import { formatMoney } from '$lib/utils/money';
  import { createQuery } from '@tanstack/svelte-query';
  import {
    ArrowLeft,
    Building2,
    Mail,
    Phone,
    Globe,
    MapPin,
    Pencil,
    Wrench,
    Kanban,
    ArrowDownLeft,
    ArrowUpRight,
    ChevronRight
  } from 'lucide-svelte';
  import { companiesApi } from '$lib/api/crm';
  import { session } from '$lib/state/session.svelte';
  import { getErrorMessage } from '$lib/api/client';
  import { emailDirection as emailDirectionOf } from '$lib/utils/emailDirection';
  import CommentThread from '$lib/components/ui/CommentThread.svelte';
  import EmailActivityModal from '$lib/components/entities/EmailActivityModal.svelte';
  import OwnerDashboard from './OwnerDashboard.svelte';
  import CompanyContacts from './CompanyContacts.svelte';
  import { groupEmailThreads, newestMessage, sortMessagesDesc, threadParticipants } from '$lib/utils/emailThreads';
  import type { Activity, Company, CompanyRef, CustomFieldDef, Person } from '$lib/types';

  const {
    id,
    backLabel = 'Companies',
    onClose,
    onInvalid,
    onNavigate,
    onEdit,
    onOpenSite
  }: {
    id: number;
    // OwnersPage mounts this same view for an owner row (#/owners/<id>) —
    // "Companies" as the back-button label there would point at a list this
    // detail didn't come from, so the caller can override it.
    backLabel?: string;
    onClose: () => void;
    onInvalid: () => void;
    onNavigate: (id: number) => void;
    onEdit: (company: Company) => void;
    // Navigates to the full-page site dashboard (#/sites/<id>) for one of
    // this owner's rollup sites — only ever called for kind=owner (see the
    // rollup section below).
    onOpenSite: (siteId: number, verticalId: number) => void;
  } = $props();

  // ponytail: same badge-color recipe (and hotel-kind-means-"property"
  // label) as CompaniesPage's table — kept in sync manually, there is no
  // shared palette module yet.
  const KIND_BADGE: Record<string, { bg: string; fg: string }> = {
    hotel: { bg: '#E3F0F1', fg: '#226A73' },
    brand: { bg: '#ECF8CC', fg: '#5c7a00' },
    owner: { bg: '#FBF0DC', fg: '#8a5c00' },
    company: { bg: '#F1F0EF', fg: '#666666' }
  };
  function kindBadge(k: string) {
    return KIND_BADGE[k] ?? KIND_BADGE.company;
  }
  const KIND_LABEL: Record<string, string> = {
    hotel: 'Property',
    brand: 'Brand',
    owner: 'Owner',
    company: 'Company'
  };
  function kindLabel(k: string): string {
    return KIND_LABEL[k] ?? k;
  }
  const PARTNER_BADGE: Record<string, { bg: string; fg: string; label: string }> = {
    prospect: { bg: '#F1F0EF', fg: '#666666', label: 'Prospect' },
    in_discussion: { bg: '#FBF0DC', fg: '#8a5c00', label: 'In discussion' },
    active_partner: { bg: '#ECF8CC', fg: '#5c7a00', label: 'Active partner' },
    dormant: { bg: '#E6E9EE', fg: '#10223A', label: 'Dormant' }
  };
  const STATUS_BADGE: Record<string, { bg: string; fg: string }> = {
    open: { bg: '#ECF8CC', fg: '#5c7a00' },
    in_progress: { bg: '#FBF0DC', fg: '#8a5c00' },
    resolved: { bg: '#E6E9EE', fg: '#10223A' }
  };
  const ACTIVITY_BADGE: Record<string, { bg: string; fg: string }> = {
    note: { bg: '#E3F0F1', fg: '#226A73' },
    task: { bg: '#E6E9EE', fg: '#10223A' },
    cold_call: { bg: '#FBF0DC', fg: '#8a5c00' },
    call: { bg: '#FBF0DC', fg: '#8a5c00' },
    meeting: { bg: '#ECF8CC', fg: '#5c7a00' },
    email: { bg: '#EAE6F3', fg: '#5a4b8a' }
  };

  const detailQuery = createQuery(() => ({
    queryKey: ['company-detail', id],
    queryFn: () => companiesApi.getWithRelated(id)
  }));

  // ponytail: any fetch failure for a direct id (stale bookmark/hash,
  // deleted record, no access) bounces back to the list rather than
  // stranding the user on a dead detail screen — a proper 404-vs-other
  // distinction would need the API to expose status codes to the client.
  $effect(() => {
    if (detailQuery.isError) onInvalid();
  });

  // kind=owner gets its own dashboard (OwnerDashboard) — the rollup query
  // (companyRollup on the backend), its stat strip, sites grid and unified
  // timeline all live there now, self-contained the same way SiteDashboard
  // is. This branch only needs to know the kind to pick which body to show.
  const isOwner = $derived(detailQuery.data?.kind === 'owner');

  const customFieldDefs = $derived(session.customFieldsFor('companies', detailQuery.data?.vertical_id));

  function formatCustomValue(def: CustomFieldDef, value: unknown): string {
    if (value == null || value === '') return '—';
    if (Array.isArray(value)) return value.join(', ');
    if (def.type === 'checkbox') return value ? 'Yes' : 'No';
    return String(value);
  }

  // Hides the custom-fields card entirely once every one of its values is
  // blank, rather than showing a card full of "—" (problem 3) — applies to
  // every kind this view still renders custom fields for (owner no longer
  // does, see OwnerDashboard).
  const hasCustomFieldValues = $derived(
    customFieldDefs.some((def) => formatCustomValue(def, detailQuery.data?.custom[def.key]) !== '—')
  );

  function personName(p: Person): string {
    return `${p.first_name} ${p.last_name}`.trim() || p.email || '(no name)';
  }
  function initials(name: string): string {
    return (
      name
        .trim()
        .split(/\s+/)
        .map((s) => s[0])
        .filter(Boolean)
        .slice(0, 2)
        .join('')
        .toUpperCase() || '?'
    );
  }

  function timeAgo(iso: string): string {
    const ms = Date.now() - new Date(iso).getTime();
    const min = Math.floor(ms / 60000);
    if (min < 1) return 'now';
    if (min < 60) return `${min}m`;
    const hr = Math.floor(min / 60);
    if (hr < 24) return `${hr}h`;
    const days = Math.floor(hr / 24);
    if (days < 7) return `${days}d`;
    return new Date(iso).toLocaleDateString();
  }

  function goto(ref: CompanyRef | Company) {
    onNavigate(ref.id);
  }

  let emailDetailRow = $state<Activity | null>(null);

  // Thread grouping — same client-side rule as ActivitiesPage (see
  // emailThreads.ts): consecutive `email` activities sharing a thread_key
  // collapse into one expandable card.
  let expandedThreads = $state<Set<string>>(new Set());
  function toggleThread(key: string) {
    const next = new Set(expandedThreads);
    if (next.has(key)) next.delete(key);
    else next.add(key);
    expandedThreads = next;
  }
</script>

<div class="mx-auto max-w-[1000px] px-4 py-5 md:px-8 md:py-6">
  <button
    type="button"
    class="mb-3.5 inline-flex items-center gap-1.5 text-[12.5px] font-semibold text-toyama-teal"
    onclick={onClose}
  >
    <ArrowLeft class="h-4 w-4" /> {backLabel}
  </button>

  {#if detailQuery.isLoading}
    <p class="tym-loading-state">Loading company&hellip;</p>
  {:else if detailQuery.isError}
    <p class="tym-error-state">{getErrorMessage(detailQuery.error)}</p>
  {:else if detailQuery.data}
    {@const company = detailQuery.data}
    {@const related = company.related}
    {@const badge = kindBadge(company.kind)}

    <div class="mb-5 flex flex-wrap items-start gap-4">
      <span
        class="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl"
        style="background:{badge.bg}; color:{badge.fg}"
      >
        <Building2 class="h-6 w-6" />
      </span>
      <div class="min-w-0 flex-1">
        <div class="flex flex-wrap items-center gap-2.5">
          <h2 class="font-heading text-[22px] font-bold leading-tight tracking-tight text-navy">
            {company.name}
          </h2>
          <span class="tym-badge" style="background:{badge.bg}; color:{badge.fg}">{kindLabel(company.kind)}</span>
          {#if company.partner_status}
            {@const ps = PARTNER_BADGE[company.partner_status]}
            <span class="tym-badge" style="background:{ps.bg}; color:{ps.fg}">{ps.label}</span>
          {/if}
        </div>
        <div class="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1.5 text-[13px] text-[#666]">
          {#if related?.owner}
            <button type="button" class="inline-flex items-center gap-1.5 font-medium text-toyama-teal underline decoration-1 underline-offset-2" onclick={() => goto(related.owner!)}>
              Owner: {related.owner.name}
            </button>
          {/if}
          {#if related?.brand}
            <button type="button" class="inline-flex items-center gap-1.5 font-medium text-toyama-teal underline decoration-1 underline-offset-2" onclick={() => goto(related.brand!)}>
              Brand: {related.brand.name}
            </button>
          {/if}
          {#if company.city}
            <span class="inline-flex items-center gap-1.5"><MapPin class="h-3.5 w-3.5 text-dark-gray" />{company.city}</span>
          {/if}
        </div>
      </div>
      <button type="button" class="btn-secondary !h-10 shrink-0" onclick={() => onEdit(company)}>
        <Pencil class="h-4 w-4" /> Edit
      </button>
    </div>

    <div class="mb-5 flex flex-wrap gap-x-5 gap-y-1.5 text-[13px] text-[#666]">
      {#if company.phone}<span class="inline-flex items-center gap-1.5"><Phone class="h-3.5 w-3.5 text-dark-gray" />General company phone: {company.phone}</span>{/if}
      {#if company.email}<span class="inline-flex items-center gap-1.5"><Mail class="h-3.5 w-3.5 text-dark-gray" />General company email: {company.email}</span>{/if}
      {#if company.website}<span class="inline-flex items-center gap-1.5"><Globe class="h-3.5 w-3.5 text-dark-gray" />{company.website}</span>{/if}
    </div>

    <div class="tym-card mb-5 px-4 py-4">
      <CompanyContacts companyId={company.id} verticalId={company.vertical_id} people={related?.people ?? []} />
    </div>

    {#if isOwner}
      <!-- Owner rollup: stat strip, sites grid and unified cross-site
           timeline all live in their own component — see companyRollup on
           the backend and the "owner vs. site dashboard parity" spec. -->
      <OwnerDashboard ownerId={company.id} onOpenSite={(siteId) => onOpenSite(siteId, company.vertical_id)} />
    {:else}
    <div class="grid grid-cols-1 gap-5 lg:grid-cols-[320px_1fr]">
      <!-- left: details -->
      <div class="flex min-w-0 flex-col gap-4">
        {#if customFieldDefs.length && hasCustomFieldValues}
          <div class="tym-card px-[18px] py-4">
            <div class="mb-2.5 text-[11px] font-semibold uppercase tracking-[.08em] text-toyama-teal">Custom fields</div>
            {#each customFieldDefs as def (def.id)}
              <div class="flex justify-between gap-3 border-t border-[#F0F0F0] py-2 first:border-t-0">
                <span class="text-[12.5px] text-dark-gray">{def.label}</span>
                <span class="text-right text-[13px] font-medium text-navy">{formatCustomValue(def, company.custom[def.key])}</span>
              </div>
            {/each}
          </div>
        {/if}

        <div class="tym-card px-[18px] py-4">
          <div class="mb-2.5 text-[11px] font-semibold uppercase tracking-[.08em] text-toyama-teal">Deals</div>
          {#each related?.deals ?? [] as d (d.id)}
            <div class="flex items-center justify-between gap-2 py-1.5">
              <div class="min-w-0">
                <div class="truncate text-[13px] font-semibold text-navy">{d.name}</div>
                <div class="truncate text-[11.5px] capitalize text-dark-gray">{d.stage || '—'}</div>
              </div>
              <span class="shrink-0 font-heading text-[13.5px] font-bold text-toyama-teal">{formatMoney(d.amount, d.currency)}</span>
            </div>
          {:else}
            <p class="py-1 text-[12.5px] text-dark-gray">No deals yet.</p>
          {/each}
        </div>

        {#if company.kind === 'brand'}
          <div class="tym-card px-[18px] py-4">
            <div class="mb-2.5 text-[11px] font-semibold uppercase tracking-[.08em] text-toyama-teal">Properties</div>
            {#each related?.properties ?? [] as prop (prop.id)}
              <button type="button" class="flex w-full items-center gap-2.5 rounded-lg py-1.5 text-left hover:bg-toyama-light-carbon" onclick={() => goto(prop)}>
                <Building2 class="h-4 w-4 shrink-0 text-toyama-teal" />
                <span class="min-w-0 flex-1 truncate text-[13px] font-semibold text-navy">{prop.name}</span>
                <span class="shrink-0 text-[11px] text-dark-gray">{kindLabel(prop.kind)}</span>
              </button>
            {:else}
              <p class="py-1 text-[12.5px] text-dark-gray">No linked properties yet.</p>
            {/each}
          </div>
        {/if}
      </div>

      <!-- right: activity + issues -->
      <div class="flex min-w-0 flex-col gap-5">
        <div>
          <div class="mb-2.5 flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[.08em] text-dark-gray">
            <Wrench class="h-3.5 w-3.5" /> Open issues
          </div>
          <div class="flex flex-col gap-2">
            {#each related?.issues ?? [] as issue (issue.id)}
              {@const sb = STATUS_BADGE[issue.status] ?? STATUS_BADGE.open}
              <div class="tym-card px-4 py-3">
                <div class="flex items-center justify-between gap-2">
                  <span class="truncate text-[13.5px] font-semibold text-navy">{issue.title}</span>
                  <span class="tym-badge shrink-0" style="background:{sb.bg}; color:{sb.fg}">{issue.status.replace('_', ' ')}</span>
                </div>
                {#if issue.body}<p class="mt-1 line-clamp-2 text-[12.5px] text-[#666]">{issue.body}</p>{/if}
              </div>
            {:else}
              <p class="text-[12.5px] text-dark-gray">No open issues for this property.</p>
            {/each}
          </div>
        </div>

        <div>
          <div class="mb-2.5 flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[.08em] text-dark-gray">
            <Kanban class="h-3.5 w-3.5" /> Recent touchpoints
          </div>
          {#snippet activityCard(a: Activity)}
            {@const ab = ACTIVITY_BADGE[a.type] ?? ACTIVITY_BADGE.note}
            {@const isEmail = a.type === 'email'}
            <svelte:element
              this={isEmail ? 'button' : 'div'}
              type={isEmail ? 'button' : undefined}
              role={isEmail ? 'button' : undefined}
              class="tym-card w-full px-4 py-3 text-left"
              onclick={isEmail ? () => (emailDetailRow = a) : undefined}
            >
              <div class="flex items-center gap-2">
                {#if isEmail}<Mail class="h-3.5 w-3.5 shrink-0" style="color:{ab.fg}" />{/if}
                <span class="text-[11px] font-bold uppercase tracking-[.04em]" style="color:{ab.fg}">{a.type.replace('_', ' ')}</span>
                {#if a.status === 'scheduled'}
                  <span class="tym-badge" style="background:#E3F0F1; color:#226A73">Scheduled</span>
                {/if}
                {#if isEmail}
                  <span class="tym-badge" style="background:#E6E9EE; color:#666">Synced</span>
                {/if}
                <span class="flex-1"></span>
                <span class="text-[11px] text-dark-gray">{timeAgo(a.email?.sent_at ?? a.created_at)}</span>
              </div>
              {#if a.title}<div class="mt-1 text-[13.5px] font-medium text-toyama-heavy-carbon">{a.title}</div>{/if}
              {#if isEmail && a.email}
                <div class="mt-1 flex items-center gap-1 text-[11.5px] text-dark-gray">
                  {#if emailDirectionOf(a, related?.people ?? [], company) === 'received'}
                    <ArrowDownLeft class="h-3 w-3 shrink-0" />
                  {:else}
                    <ArrowUpRight class="h-3 w-3 shrink-0" />
                  {/if}
                  <span class="truncate">{a.email.from_addr}</span>
                </div>
                {#if a.email.snippet}<p class="mt-1 line-clamp-2 text-[12.5px] text-[#666]">{a.email.snippet}</p>{/if}
              {:else if a.body}
                <p class="mt-1 line-clamp-2 text-[12.5px] text-[#666]">{a.body}</p>
              {/if}
              <div class="mt-1.5 text-[11px] text-dark-gray">{a.created_by}</div>
            </svelte:element>
          {/snippet}
          <div class="flex flex-col gap-2">
            {#each groupEmailThreads(related?.activities ?? []) as entry (entry.kind === 'thread' ? `t:${entry.key}` : `a:${entry.activity.id}`)}
              {#if entry.kind === 'thread' && entry.messages.length > 1}
                {@const newest = newestMessage(entry.messages)}
                {@const open = expandedThreads.has(entry.key)}
                <div class="tym-card overflow-hidden">
                  <button
                    type="button"
                    class="flex min-h-11 w-full items-start gap-2.5 px-4 py-3 text-left hover:bg-toyama-light-carbon"
                    onclick={() => toggleThread(entry.key)}
                    aria-expanded={open}
                  >
                    <ChevronRight class="mt-0.5 h-4 w-4 shrink-0 text-dark-gray transition-transform {open ? 'rotate-90' : ''}" />
                    <Mail class="mt-0.5 h-4 w-4 shrink-0" style="color:{ACTIVITY_BADGE.email.fg}" />
                    <div class="min-w-0 flex-1">
                      <div class="flex flex-wrap items-center gap-2">
                        <span class="truncate text-[13.5px] font-medium text-toyama-heavy-carbon"
                          >{newest.email?.subject || newest.title || '(no subject)'}</span
                        >
                        <span class="tym-badge shrink-0" style="background:#EAE6F3; color:#5a4b8a"
                          >{entry.messages.length} messages</span
                        >
                      </div>
                      <div class="mt-0.5 truncate text-[11.5px] text-dark-gray">{threadParticipants(entry.messages)}</div>
                    </div>
                    <span class="shrink-0 text-[11px] text-dark-gray">{timeAgo(newest.email?.sent_at ?? newest.created_at)}</span>
                  </button>
                  {#if open}
                    <div class="flex flex-col gap-1.5 border-t border-[#F0F0F0] bg-[#FBFBFA] px-2.5 py-2">
                      {#each sortMessagesDesc(entry.messages) as m (m.id)}
                        {@render activityCard(m)}
                      {/each}
                    </div>
                  {/if}
                </div>
              {:else}
                {@render activityCard(entry.kind === 'thread' ? entry.messages[0] : entry.activity)}
              {/if}
            {:else}
              <p class="text-[12.5px] text-dark-gray">No touchpoints logged yet.</p>
            {/each}
          </div>
        </div>
      </div>
    </div>
    {/if}

    <div class="mt-5">
      <CommentThread entityType="company" entityId={company.id} />
    </div>
  {/if}
</div>

{#if emailDetailRow}
  <EmailActivityModal
    activity={emailDetailRow}
    onClose={() => (emailDetailRow = null)}
    onDismissed={() => (emailDetailRow = null)}
  />
{/if}
